import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  Row,
  Col,
  Stack,
  Form,
  Container,
  Button,
  Popover,
  OverlayTrigger,
} from "react-bootstrap";
import { Link } from "react-router-dom";

import { useMaintenanceUpdateMutation } from "api/memberPortal/project/maintenance/maintenance.api";
import {
  MAINTENANCE_STATUSES,
  getMaintenanceStatusDisplay,
} from "api/memberPortal/project/maintenance/maintenance.types";
import { useProductUpdateMutation } from "api/memberPortal/project/product/product.api";
import { useProgressPointUpdateMutation } from "api/memberPortal/project/progressPoints/progressPoints.api";
import { handleAlertAndMailer } from "components/Products/Helper/functions";
import DeleteModal from "components/UI/DeleteModal";
import { useAuth } from "hooks/useAuth";
import { getISOCurrentDate, calculateActiveDaysInAM } from "utils/timeUtils";

export default function AMHeader({
  product,
  maintenance,
  refetch,
  progressPoints,
  setAlert,
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingCompletion, setIsEditingCompletion] = useState(false);
  const [editedAm, setEditedAM] = useState({});
  const [isReject, setIsReject] = useState(false);
  const [isDeny, setIsDeny] = useState(false);
  const [protectionClaims, setProtectionClaims] = useState([]);

  const [triggerProgressPointUpdate] = useProgressPointUpdateMutation();
  const [triggerProductUpdate] = useProductUpdateMutation();
  const [triggerMaintenanceUpdate] = useMaintenanceUpdateMutation();

  const { authToken, csrfToken, permissions } = useAuth();

  function refreshPage() {
    window.location.reload();
  }

  const removeFromAm = async () => {
    try {
      await triggerProductUpdate({
        product_id: product.product_id,
        assigned_lab: product?.am_initial_cctl?.org_id,
        am_initial_cctl: null,
        submission_type: "Evaluation",
        status: "Certified",
        current_assurance_maintenance: null,
      }).unwrap();

      await triggerMaintenanceUpdate({
        maintenance_id: maintenance.maintenance_id,
        status: "Terminated",
        completion_date: getISOCurrentDate(),
      }).unwrap();

      setIsDeny(false);
      setShowDeleteModal(false);
      setAlert({
        message: "Successfully Removed from Assurance Maintenance!",
        variant: "success",
      });
      refetch();
    } catch (error) {
      setIsDeny(false);
      setShowDeleteModal(false);
      console.error("Error Deleting Assurance Maintenance:", error);
      setAlert({
        message: "Error Deleting Assurance Maintenance",
        variant: "danger",
      });
    }
  };

  const rejectAm = async () => {
    try {
      await triggerMaintenanceUpdate({
        maintenance_id: maintenance.maintenance_id,
        status: "Rejected",
      })
        .unwrap()
        .then(() => {
          setIsReject(false);
          setShowDeleteModal(false);
          setAlert({
            message: "Successfully Rejected Assurance Maintenance!",
            variant: "success",
          });
          refetch();
        });
    } catch (error) {
      console.error("Error Rejecting Assurance Maintenance:", error);
      setAlert({
        message: "Error Rejecting Assurance Maintenance",
        variant: "danger",
      });
    }
  };

  // TODO: we have some weird stuff going on with this email, we are not sending it but creating an alert for it-
  // I think we just need to send an alert here but need to talk with Mike 7/8/25
  const handleCertifyAlertMail = () => {
    handleAlertAndMailer(csrfToken, authToken, {
      alert_type_id: product?.product_id,
      alert_type: "Product",
      alert_source: "Assurance Maintenance",
      subject: `VID${product?.v_id} – IAR Concurrence`,
      recipients: {
        to: [],
        cc: ["Validators", "NIAP Management", "AM Approver"],
      },
      alert_text: `Assurance Maintenance for VID${product?.v_id} Posted.`,
    });
  };

  const certifyAm = async () => {
    try {
      await triggerMaintenanceUpdate({
        maintenance_id: maintenance.maintenance_id,
        status: "Approved",
        niap_approval_date: getISOCurrentDate(),
        completion_date: getISOCurrentDate(),
      }).unwrap();

      await triggerProductUpdate({
        product_id: product.product_id,
        assigned_lab: product?.am_initial_cctl?.org_id,
        am_initial_cctl: null,
        submission_type: "Evaluation",
        status: "Certified",
        current_assurance_maintenance: null,
      }).unwrap();

      const progressResponse = await triggerProgressPointUpdate({
        maintenance: maintenance?.maintenance_id,
        progress_point: 4,
        status: "Completed",
      }).unwrap();

      if (progressResponse.status === "Update Already Exists") {
        refreshPage();
        return;
      }

      handleCertifyAlertMail();
      refetch();
      setAlert({
        message: "Successfully Certified Assurance Maintenance!",
        variant: "success",
      });
    } catch (error) {
      console.error("Error certifying AM:", error);
      setAlert({
        message: "Error certifying Assurance Maintenance",
        variant: "danger",
      });
    }
  };

  //update product with new assurance maintenance date
  const submitDataChange = async (date) => {
    try {
      await triggerProductUpdate({
        product_id: product.product_id,
        assurance_maintenance_date: date,
      })
        .unwrap()
        .then(() => {
          refetch();
        });
    } catch (error) {
      console.error("Error Updating Assurance Maintenance Date:", error);
      setAlert({
        message: "Error Updating Assurance Maintenance Date",
        variant: "danger",
      });
    }
  };

  const handleChange = (e) => {
    setEditedAM({ ...editedAm, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (Object.keys(editedAm).length > 0) {
      try {
        await triggerMaintenanceUpdate({
          maintenance_id: maintenance.maintenance_id,
          ...editedAm,
        })
          .unwrap()
          .then(() => {
            setAlert({
              message: "Successfully Updated Assurance Maintenance!",
              variant: "success",
            });
            setIsEditing(false);
            setIsEditingCompletion(false);
            refetch();
          });
      } catch (error) {
        console.error("Error Updating Assurance Maintenance:", error);
        setAlert({
          message: "Error Updating Assurance Maintenance",
          variant: "danger",
        });
      }
    }
  };

  useEffect(() => {
    getPPs(product.product_id);
  }, [product.product_id]);

  const getPPs = (product_id) => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/protection-profiles/protection_profiles_by_name/?product=${product_id}`,
        {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
        },
      )
      .then((response) => {
        setProtectionClaims(response.data);
      })
      .catch((error) => console.log(error));
  };

  return (
    <Container>
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        type={"Assurance Maintenance"}
        deleteFunction={isDeny ? removeFromAm : rejectAm}
        isDeny={isDeny}
        isReject={isReject}
      />
      <Row>
        <Col sm={12} lg={7}>
          <h3 className="fw-bold text-primary">
            ASSURANCE MAINTENANCE DETAILS:{" "}
          </h3>
        </Col>
        {progressPoints[3]?.status === "In Progress" &&
          permissions?.role_type === "NIAP" && (
            <>
              <Col md={12} lg={3}>
                <Form.Group
                  className="mb-3"
                  controlId="assurance_maintenance_date"
                >
                  <Form.Label className="small-font fw-bold">
                    Select Date for Assurance Maintenance:
                  </Form.Label>
                  <Form.Control
                    style={{ width: "fit-content" }}
                    type="date"
                    name="assurance_maintenance_date"
                    defaultValue={product?.assurance_maintenance_date
                      ?.toString()
                      .substring(0, 10)}
                    onChange={(e) => submitDataChange(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={12} lg={2} className="m-auto">
                <OverlayTrigger
                  trigger={["hover", "hover"]}
                  rootClose
                  disabled
                  placement="bottom"
                  key="bottom"
                  overlay={
                    maintenance?.maintenance_description !== null &&
                    maintenance?.change_summary !== null &&
                    product?.assurance_maintenance_date !== null ? (
                      <span></span>
                    ) : (
                      <Popover>
                        <Popover.Body>
                          <p className="m-0">
                            Before Posting to PCL, please make sure to include
                            an updated Maintenance Description, Change Summary,
                            and date for Assurance Maintenance.
                          </p>
                        </Popover.Body>
                      </Popover>
                    )
                  }
                >
                  <div style={{ width: "fit-content" }}>
                    <Button
                      size="md"
                      variant="warning"
                      disabled={
                        maintenance?.maintenance_description === null ||
                        maintenance?.change_summary === null ||
                        product?.assurance_maintenance_date === null
                      }
                      onClick={certifyAm}
                    >
                      Post to PCL
                    </Button>
                  </div>
                </OverlayTrigger>
              </Col>
            </>
          )}
      </Row>
      <Row>
        <Col sm={6} className="mt-3">
          <h3 className="fw-bold">{product?.vendor_id?.name}</h3>
          <h4 className="fw-bold text-info">VID {product?.v_id}</h4>
        </Col>
        <Col sm={6} className="mt-3 d-flex flex-column align-items-center">
          <Stack className="border-start border-secondary ps-2 my-2">
            {!isEditing && permissions.role_type === "NIAP" && (
              <div className="d-flex justify-content-end">
                <EditIcon
                  color="action"
                  className="clickable"
                  style={{ fontSize: "1.5rem" }}
                  onClick={() => setIsEditing(true)}
                />
              </div>
            )}
            {isEditing && (
              <div className="d-flex justify-content-end">
                <SaveIcon
                  color="action"
                  className="clickable"
                  style={{ fontSize: "1.5rem" }}
                  onClick={() => handleSave()}
                />
              </div>
            )}
            {isEditing ? (
              <>
                <Form.Label>Release: </Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  onChange={handleChange}
                  defaultValue={maintenance?.title}
                />
              </>
            ) : (
              <h4 className="fw-bold">Release: {maintenance?.title}</h4>
            )}
          </Stack>
        </Col>
        <Col md={2} className="border-dark border-bottom border-top">
          <Stack className="border-secondary ps-2 my-2">
            <h5>Protection Profiles</h5>
            {protectionClaims?.map((projpp) => (
              <OverlayTrigger
                trigger={["hover", "focus"]}
                overlay={
                  <Popover>
                    <Popover.Body>{projpp?.pp__pp_name}</Popover.Body>
                  </Popover>
                }
                key={projpp?.pp__pp_id}
              >
                <Link
                  to={`/community/protectionprofiles/details/${projpp?.pp__pp_id}`}
                  className="text-secondary"
                >
                  <p className="py-0 my-0">{projpp?.pp__pp_short_name}</p>
                </Link>
              </OverlayTrigger>
            ))}
          </Stack>
        </Col>
        <Col md={2} className="border-dark border-bottom border-top">
          <Stack className="border-start border-secondary ps-2 my-2">
            <h5>Days In AM</h5>
            <h5 className="text-secondary">
              {calculateActiveDaysInAM(maintenance)}
            </h5>
            <h5 className="text-secondary"></h5>
          </Stack>
        </Col>
        <Col md={2} className="border-dark border-bottom border-top">
          <Stack className="border-start border-secondary ps-2 my-2">
            <h5>IAR Received:</h5>
            {maintenance?.iar_received ? (
              <h5 className="text-secondary">
                {moment.utc(maintenance?.iar_received).format("MM/DD/YYYY")}
              </h5>
            ) : (
              <h5 className="text-secondary">IAR Not Uploaded</h5>
            )}
            <h5 className="text-secondary"></h5>
          </Stack>
        </Col>
        <Col className="border-dark border-bottom border-top">
          <Stack className="border-start border-secondary ps-2 my-2">
            <h5>AM Status</h5>
            <h5 className="text-secondary">
              {getMaintenanceStatusDisplay(maintenance.status)}
            </h5>
            {permissions?.role_type === "NIAP" &&
              product?.status === "In Assurance Maintenance" && (
                <Button
                  variant="warning"
                  className="rounded-pill small-font p-1 mt-2 mb-2"
                  onClick={() => {
                    setIsDeny(true);
                    setIsReject(false);
                    setShowDeleteModal(true);
                  }}
                >
                  Remove From Assurance Maintenance
                </Button>
              )}
            {permissions?.role_type === "NIAP" &&
              maintenance?.status === "Pending" && (
                <Button
                  variant="danger"
                  className="rounded-pill small-font p-1 mt-2 mb-2"
                  onClick={() => {
                    setIsReject(true);
                    setIsDeny(false);
                    setShowDeleteModal(true);
                  }}
                >
                  Reject Assurance Maintenance
                </Button>
              )}
          </Stack>
        </Col>
        <Col className="border-dark border-bottom border-top">
          <Stack className="border-start border-secondary ps-2 my-2">
            {!isEditingCompletion && permissions.role_type === "NIAP" && (
              <div className="d-flex justify-content-end">
                <EditIcon
                  color="action"
                  className="clickable"
                  style={{ fontSize: "1.5rem" }}
                  onClick={() => setIsEditingCompletion(true)}
                />
              </div>
            )}
            {isEditingCompletion && (
              <div className="d-flex justify-content-end">
                <SaveIcon
                  color="action"
                  className="clickable"
                  style={{ fontSize: "1.5rem" }}
                  onClick={() => handleSave()}
                />
              </div>
            )}
            <h5>
              {maintenance.status === MAINTENANCE_STATUSES.TERMINATED
                ? `${getMaintenanceStatusDisplay(maintenance.status)} on `
                : "Completed"}
            </h5>
            {isEditingCompletion ? (
              <>
                <Form.Label>Date: </Form.Label>
                <Form.Control
                  type="date"
                  name="completion_date"
                  onChange={handleChange}
                  defaultValue={
                    maintenance?.completion_date
                      ? moment
                          .utc(maintenance.completion_date)
                          .format("YYYY-MM-DD")
                      : ""
                  }
                />
              </>
            ) : (
              <h5 className="text-secondary">
                {maintenance?.completion_date
                  ? moment
                      .utc(maintenance?.completion_date)
                      .format("MM/DD/YYYY")
                  : "TBD"}
              </h5>
            )}
          </Stack>
        </Col>
      </Row>
    </Container>
  );
}
